import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { TraditionalLTCQuoteWithROI, QuoteWithROI } from '@/features/shop-insurance';
import { makeSelectIsInsuranceQuoteLoading } from '@/model/selectors';
import { isLifeInsuranceQuote } from '@/util/quoteTypeGuards';

type PolicyQuote = QuoteWithROI | TraditionalLTCQuoteWithROI;

interface UsePolicyLoadingStateParams {
  quote: PolicyQuote;
  client: Client;
}

interface UsePolicyLoadingStateReturn {
  isLoading: boolean;
  insuranceQuoteId: string;
  fundingSourceId: string | null;
}

export function usePolicyLoadingState({
  quote,
  client,
}: UsePolicyLoadingStateParams): UsePolicyLoadingStateReturn {
  const isLifeInsurance = isLifeInsuranceQuote(quote);

  const insuranceQuoteId = isLifeInsurance
    ? (quote as QuoteWithROI).insuranceQuoteId
    : (quote as TraditionalLTCQuoteWithROI).traditionalLTCQuoteId;

  const fundingSourceId = useMemo(() => {
    if (!client?.multipleFundingSources?.ltcPolicy) return null;

    for (const [fundingSourceId, policy] of Object.entries(
      client.multipleFundingSources.ltcPolicy,
    )) {
      if (policy.insuranceQuoteId === insuranceQuoteId) {
        return fundingSourceId;
      }
    }
    return null;
  }, [client?.multipleFundingSources?.ltcPolicy, insuranceQuoteId]);

  const isLoading: boolean = useSelector(
    makeSelectIsInsuranceQuoteLoading(insuranceQuoteId, fundingSourceId),
  );

  return {
    isLoading,
    insuranceQuoteId,
    fundingSourceId,
  };
}
